import type { ViewBounds } from "@edenapp/types";

/**
 * Eden App Frame Injector
 *
 * Main orchestrator for the app frame system.
 * This script is injected into app views to apply frame runtime behavior.
 */

import {
  closeAppFrameView,
  minimizeAppFrameView,
  setupCloseButton,
  setupMinimizeButton,
  setupToggleModeButton,
  toggleAppFrameViewMode,
} from "./button-handlers.js";
import { setupLayoutCompass } from "./layout-compass.js";
import {
  createOverlay,
  getAppName,
  injectOverlay,
  setTitle,
  setupDarkMode,
} from "./ui-builder.js";
import { syncEdenFrameBounds } from "./utils.js";
import { setupWindowDragging } from "./window-dragging.js";
import { setupWindowResizing } from "./window-resizing.js";

(() => {
  // Initialize edenFrame object if not exists
  if (!window.edenFrame) {
    window.edenFrame = {
      setTitle: (_title: string) => {
        /* will be set later */
      },
      resetTitle: () => {
        /* will be set later */
      },
      close: async () => {
        /* will be set later */
      },
      minimize: async () => {
        /* will be set later */
      },
      toggleMode: async () => {
        /* will be set later */
      },
      _internal: {
        appId: "",
        appName: "" as string | Record<string, string>,
        injected: false,
        config: {},
        currentMode: "tiled",
        bounds: { x: 0, y: 0, width: 0, height: 0 },
      },
    };
  }
  if (!window.edenFrame.setTitle) {
    window.edenFrame.setTitle = () => {
      /* no-op until initialized */
    };
  }
  if (!window.edenFrame.resetTitle) {
    window.edenFrame.resetTitle = () => {
      /* no-op until initialized */
    };
  }
  if (!window.edenFrame.close) {
    window.edenFrame.close = async () => {
      /* no-op until initialized */
    };
  }
  if (!window.edenFrame.minimize) {
    window.edenFrame.minimize = async () => {
      /* no-op until initialized */
    };
  }
  if (!window.edenFrame.toggleMode) {
    window.edenFrame.toggleMode = async () => {
      /* no-op until initialized */
    };
  }

  // Check if already injected
  if (window.edenFrame._internal.injected) {
    return;
  }
  window.edenFrame._internal.injected = true;

  let appName: string = "App";
  let currentLocale = "en";
  let closeLayoutCompass: (() => void) | null = null;
  let refreshLayoutCompassLocale: (() => void) | null = null;

  // Track current window bounds for floating windows
  // Using an object reference so we can update it and have the change reflected in all modules
  const currentBoundsRef: {
    current: { x: number; y: number; width: number; height: number } | null;
  } = { current: null };

  let cleanupDrag: (() => void) | null = null;
  let cleanupResize: (() => void) | null = null;

  const configuredAppFrameMode =
    window.edenFrame._internal.config.injections?.appFrame;
  const appFrameMode =
    configuredAppFrameMode === "window-only" ? "window-only" : "full";
  const includeSystemTopBar = appFrameMode === "full";

  // Only create the Eden title bar in full mode.
  const overlay = includeSystemTopBar
    ? createOverlay(window.edenFrame._internal.config)
    : null;

  const syncOverlayMode = (): void => {
    if (!overlay || !window.edenFrame) return;
    const isTiled = window.edenFrame._internal.currentMode === "tiled";
    overlay.classList.toggle("eden-app-frame-tiled", isTiled);
    const title = overlay.querySelector<HTMLElement>("#eden-app-frame-title");
    if (!isTiled) closeLayoutCompass?.();
    if (!title) return;
    if (isTiled) {
      title.setAttribute("role", "button");
      title.setAttribute("aria-haspopup", "dialog");
      title.setAttribute("aria-expanded", "false");
      title.tabIndex = 0;
    } else {
      title.removeAttribute("role");
      title.removeAttribute("tabindex");
      title.removeAttribute("aria-haspopup");
      title.removeAttribute("aria-expanded");
    }
  };

  // Setup handlers after overlay is injected
  const setupHandlers = (): void => {
    // Get appId from injected config
    if (window.edenFrame) {
      const rawName = window.edenFrame._internal.appName;
      appName = getAppName(rawName, "en");

      if (includeSystemTopBar) {
        // Async fetch locale for title text
        window.edenAPI
          .shellCommand("i18n/get-locale", {})
          .then((response: { locale: string }) => {
            const locale = response?.locale;
            currentLocale = locale || "en";
            appName = getAppName(rawName, currentLocale);
            setTitle(appName);
            refreshLayoutCompassLocale?.();
          })
          .catch(() => {
            appName = getAppName(rawName, "en");
            setTitle(appName);
          });

        // Set initial title (english fallback) to avoid empty title while fetching
        setTitle(appName);

        // Subscribe to locale changes for live updates
        window.edenAPI.subscribe(
          "i18n/locale-changed",
          (data: { locale: string }) => {
            const newLocale = data.locale;
            currentLocale = newLocale;
            const currentName = getAppName(rawName, newLocale);
            setTitle(currentName);
            appName = currentName;
            refreshLayoutCompassLocale?.();
          },
        );
      }
    }

    if (includeSystemTopBar) {
      // Setup system top bar button handlers
      setupCloseButton();
      setupMinimizeButton();
      setupToggleModeButton();
      if (overlay) {
        const layoutCompass = setupLayoutCompass(overlay, {
          getLocale: () => currentLocale,
          getMode: () => window.edenFrame?._internal.currentMode ?? "floating",
        });
        closeLayoutCompass = layoutCompass.close;
        refreshLayoutCompassLocale = layoutCompass.refreshLocale;
        syncOverlayMode();
      }
    }

    // Setup floating window controls
    setupFloatingWindowControls();

    window.edenAPI.subscribe(
      "view/mode-changed",
      (data: { mode: "tiled" | "floating"; bounds: ViewBounds }) => {
        const edenFrame = window.edenFrame;
        if (!edenFrame) {
          return;
        }

        const { mode, bounds } = data;

        // Update window mode and bounds
        edenFrame._internal.currentMode = mode;
        if (bounds && bounds.x !== undefined) {
          edenFrame._internal.bounds = bounds;
          currentBoundsRef.current = { ...bounds };
        }

        // Re-setup controls for new mode
        syncOverlayMode();
        setupFloatingWindowControls();
      },
    );
  };

  const setupFloatingWindowControls = (): void => {
    const edenFrame = window.edenFrame;
    if (!edenFrame) {
      return;
    }

    const windowMode = edenFrame._internal.currentMode;
    const windowConfig = edenFrame._internal.config;
    const initialBounds = edenFrame._internal.bounds;

    // Clean up previous controls if they exist (crucial for mode switching)
    if (cleanupDrag) {
      cleanupDrag();
      cleanupDrag = null;
    }
    if (cleanupResize) {
      cleanupResize();
      cleanupResize = null;
    }

    // Only enable dragging/resizing for floating windows
    if (windowMode !== "floating") {
      // If we are not floating, we just returned after cleanup, so handles are gone.
      return;
    }

    // Initialize current bounds from the actual bounds set by ViewManager
    if (initialBounds && initialBounds.x !== undefined) {
      currentBoundsRef.current = { ...initialBounds };
    } else if (windowConfig.defaultSize) {
      // Fallback to config if no initial bounds provided
      const workspaceX = 0;
      const workspaceY = 0;

      const x = windowConfig.defaultPosition?.x || 0;
      const y = windowConfig.defaultPosition?.y || 0;

      currentBoundsRef.current = {
        x: workspaceX + x,
        y: workspaceY + y,
        width: windowConfig.defaultSize.width || 800,
        height: windowConfig.defaultSize.height || 600,
      };
    }

    // Check if dragging is allowed
    const isMovable = windowConfig.movable !== false; // default true
    const isResizable = windowConfig.resizable !== false; // default true

    // Setup window dragging
    if (isMovable) {
      if (overlay) {
        cleanupDrag = setupWindowDragging(overlay, currentBoundsRef);
      } else {
        cleanupDrag = setupWindowDragging(document, currentBoundsRef, {
          dragRegionSelector: "[data-eden-drag-region]",
        });
      }
    }

    // Setup window resizing
    if (isResizable) {
      cleanupResize = setupWindowResizing(windowConfig, currentBoundsRef);
    }
  };

  // Listen for bounds updates from main process (during mouse drag/resize)
  // This keeps currentBounds in sync even when main process is controlling movement
  window.edenAPI.subscribe("view/bounds-updated", (newBounds) => {
    currentBoundsRef.current = { ...newBounds };
    syncEdenFrameBounds(newBounds);
  });

  // Inject overlay and setup handlers
  if (overlay) {
    injectOverlay(overlay, () => {
      setupDarkMode(overlay);
      setupHandlers();
    });
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupHandlers);
  } else {
    setupHandlers();
  }

  // Expose API for apps to update the title
  window.edenFrame.setTitle = (title: string) => {
    setTitle(title);
  };

  // Expose resetTitle immediately (using English/default name initially)
  window.edenFrame.resetTitle = () => {
    setTitle(appName);
  };

  // Expose app frame actions for custom title bars.
  window.edenFrame.close = () => closeAppFrameView();
  window.edenFrame.minimize = () => minimizeAppFrameView();
  window.edenFrame.toggleMode = () => toggleAppFrameViewMode();
})();
